import { useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { readFirstSignIn } from '../api/athlete-sign-in.js';
import type { Person } from '../api/use-people.js';
import { day } from './video-format.js';

/**
 * A child's consents and sign-in as the second parent sees them (canvas row D,
 * "Noa's page: facts only, no consent or access buttons", approved 17
 * October): waivers, photo and video, and activating a child's access are the
 * first parent's (ruled 17 October), so the second parent reads what was
 * decided and changes none of it.
 *
 * The sign-in part follows child-sign-in.tsx's states, as facts: on (username,
 * activated, first sign-in) or off (username, deactivated). Nothing while it
 * was never asked for, or a link is out - there is no fact yet.
 */
export function ChildFacts({ person }: { person: Person }) {
  const getToken = useAccessToken();
  const name = person.first_name;
  const signIn = person.sign_in;
  const [first, setFirst] = useState<{ signed_in: boolean; at: string | null } | null>(null);

  useEffect(() => {
    if (!signIn?.enabled) return;
    let cancelled = false;
    readFirstSignIn(getToken, person.client_id)
      .then((answer) => {
        if (!cancelled) setFirst(answer);
      })
      .catch(() => {
        // Not shown rather than shown wrong.
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, person.client_id, signIn?.enabled]);

  return (
    <>
      <Fact label="Waiver" value={person.waiver_signed_at ? `Approved ${day(person.waiver_signed_at)}` : 'Not signed'} />
      {person.image_video_allowed === null ? null : (
        <Fact label="Photo and video" value={person.image_video_allowed ? 'Yes' : 'No'} />
      )}

      {signIn?.username && (signIn.enabled || signIn.turned_off_at) ? (
        <>
          <span className="section-label">{name}&rsquo;s own sign-in</span>
          <Fact label="Username" value={signIn.username} />
          {signIn.enabled && signIn.turned_on_at ? <Fact label="Activated" value={day(signIn.turned_on_at)} /> : null}
          {signIn.enabled && first ? (
            <Fact label="First sign-in" value={first.signed_in && first.at ? day(first.at) : 'Not yet'} />
          ) : null}
          {!signIn.enabled && signIn.turned_off_at ? <Fact label="Deactivated" value={day(signIn.turned_off_at)} /> : null}
        </>
      ) : null}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}
